<script setup>
	import { onBeforeUnmount, reactive, watch } from "vue";
	import { executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "@/services";
	import TestimonialCarousel from "@/components/TestimonialCarousel.vue";
	import largeLucentaryLogoUrl from "@/assets/img/lucentary-banner-blue.svg";

	const testimonials = [
		{
			author: "Geoff A",
			role: "Engineering Leader",
			quote: "Chad is very talented technically, and he brings a wealth of experience and ability to the projects he's involved in. He also has a lot to offer in terms of project management — an effective communicator with experience across a variety of software development methodologies. Most importantly, Chad is a phenomenal mentor and teacher.",
		},
		{
			author: "Joshua L",
			role: "Founder",
			quote: "Chad is one of those rare individuals whose vision extends beyond just the project at hand. He sees the big picture, both technically, managerially, and in relationships. He knows how to train and mentor individuals to help them reach their maximum potential.",
		},
		{
			author: "Raul G",
			role: "Product Lead",
			quote: "We hired Chad for a quick turnaround SaaS project, three weeks, and he delivered exceptionally. Our team and, more importantly, the customers felt the demo prototype was professionally done and exceeded expectations. Extremely responsive, knowledgeable, and great to work with.",
		},
		{
			author: "Raj S",
			role: "VP Engineering",
			quote: "In a matter of weeks Chad assumed responsibility for the majority of the user interface. He offered fresh options and new ideas allowing us to leverage the latest technology. He also took a leadership role in defining our build process and policies.",
		},
		{
			author: "Josh S",
			role: "CTO",
			quote: "Chad is easily the smartest and most innovative architect and engineer I have worked with. Rarely do you come across someone with his breadth and command of technical knowledge. He fundamentally understands how to build robust systems that provide value to clients.",
		},
		{
			author: "Larry M",
			role: "Director of Technology",
			quote: "Chad is a remarkable architect and developer. He delivered despite the technical and management challenges of the project. With constant change in technical direction, he adapted well. I hope to work with Chad again in the future.",
		},
		{
			author: "AD A",
			role: "Operations Lead",
			quote: "We hired Chad to migrate a number of PHP applications to .NET. Without existing documentation, he worked with business users and the existing code base to re-write three applications from the ground up in a matter of weeks.",
		},
		{
			author: "DJ W",
			role: "Solutions Director",
			quote: "Chad Greer is one of the top Microsoft Software Solution Consultants in the market. He has great depth in architecting custom solutions using an Agile approach to solve complex business problems, with a natural ability to apply past experiences to new challenges.",
		},
		{
			author: "Tor N",
			role: "Software Architect",
			quote: "Chad is technically gifted, with an eye for detail, and operates well as part of a team in respect of innovative approaches to design, development and implementation. He has an impeccable work ethic and is one of the most innovative software architects I've known.",
		},
		{
			author: "Matt B",
			role: "Principal Engineer",
			quote: "Chad was my counterpart in defining and developing our company's software. He understands how to write well-formed, maintainable software, but he was a shining star because he understood how to craft an enterprise solution. Few people can develop and implement enterprise architecture as well as he does.",
		},
		{
			author: "Chad S",
			role: "Technical Lead",
			quote: "Without a doubt one of the most innovative software architects I've known and a very powerful technical leader. He has a talent for inspiring passion in others. Intelligent, with an impeccable work ethic. I highly recommend Chad Greer for any technical leadership position.",
		},
	];

	const oldWay = [
		"Take the cheapest bid and hope it works out",
		"Hand critical work to junior generalists",
		"Find out what it really costs only when it's too late",
		"Cross your fingers that it holds up in production",
	];

	const lucentaryWay = [
		"Senior engineers who've shipped real systems for 20+ years",
		"A clear, honest plan before any code is written",
		"Work done right the first time, to a high standard",
		"Documented, maintainable, and built to last",
	];

	const valueIncludes = [
		"A senior team led by Chad Greer — never offshored",
		"Decades of expertise across architecture, delivery, and code",
		"Work done to a standard of excellence you can rely on",
		"50% off your first 6 weeks as a new client",
		"Honest guidance — even if the answer is \"don't build this\"",
	];

	const faqs = [
		{
			question: "Is the consultation really free?",
			answer: "Yes. It's a 30-minute call with Chad, no obligation. Most people leave with a clear plan whether or not they decide to work with us.",
		},
		{
			question: "How much does it cost?",
			answer: "Every project is different, so we scope it on the call and give you a clear range before anything starts. New clients get 50% off their first 6 weeks.",
		},
		{
			question: "What kind of work do you take on?",
			answer: "A wide range — from architecture and new builds to improving or rescuing systems that have gone sideways. If software is important to your business, it's worth a conversation.",
		},
		{
			question: "Who actually does the work?",
			answer: "Senior engineers led by Chad Greer, who has architected and shipped enterprise software for 20+ years. No handing your project to juniors.",
		},
		{
			question: "How quickly can you start?",
			answer: "We take on a limited number of new clients each month. When there's a fit, we usually begin within a week or two of the call.",
		},
	];

	const data = reactive({
		requestMoreInfo: {
			contactInfo: {
				emailAddress: "",
				name: "",
				text: "",
			},
			isError: false,
			isProcessing: false,
			isShowing: false,
			isSuccess: false,
			topic: "",
			validationErrors: [],
		},
		openFaqIndex: 0,
	});

	function openConsultation() {
		data.requestMoreInfo.topic = "Free consultation";
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.isSuccess = false;
		data.requestMoreInfo.isProcessing = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.isShowing = true;
	}

	function closeConsultation() {
		data.requestMoreInfo.isShowing = false;
	}

	function toggleFaq(faqIndex) {
		data.openFaqIndex = data.openFaqIndex === faqIndex ? -1 : faqIndex;
	}

	async function submitConsultation() {
		data.requestMoreInfo.isProcessing = true;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.contactInfo.phoneNumber = "n/a";
		data.requestMoreInfo.contactInfo.recaptchaResponse = "skip";

		await executeServiceCall(() => selfServiceService.public.submitContactRequest(data.requestMoreInfo.contactInfo))
			.then(() => {
				data.requestMoreInfo.isSuccess = true;
			})
			.catch(error => {
				if (error?.response?.status === 400) {
					data.requestMoreInfo.validationErrors = getValidationErrorsFromResponse(error.response).map(validationMessage =>
						validationMessage === "Your E-mail Address is required."
							? "Your E-mail Address or Phone Number is required."
							: validationMessage
					);
				} else {
					console.error(error);
					data.requestMoreInfo.isError = true;
				}
			})
			.finally(() => {
				data.requestMoreInfo.isProcessing = false;
			});
	}

	function handleModalKeydown(event) {
		if (event.key === "Escape") {
			closeConsultation();
		}
	}

	watch(() => data.requestMoreInfo.isShowing, (isShowing) => {
		if (isShowing) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleModalKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleModalKeydown);
		}
	});

	onBeforeUnmount(() => {
		document.documentElement.style.overflow = "";
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
		window.removeEventListener("keydown", handleModalKeydown);
	});
</script>

<template>
	<div class="promo-c">
		<header class="site-header">
			<div class="site-header-inner">
				<span class="brand">
					<img class="brand-logo" :src="largeLucentaryLogoUrl" alt="Lucentary" />
				</span>
				<button type="button" class="btn btn-primary btn-compact" @click="openConsultation"><span class="cta-text-full">Book a free call</span><span class="cta-text-short">Book a call</span></button>
			</div>
		</header>

		<section id="book" class="hero">
			<div class="hero-decoration" aria-hidden="true">
				<span class="blob blob-one"></span>
				<span class="blob blob-two"></span>
			</div>
			<div class="hero-inner">
				<div class="hero-copy">
					<span class="offer-pill">Free consultation + 50% off your first 6 weeks</span>
					<h1 class="hero-title">When software matters, bring in the experts.</h1>
					<p class="hero-lead">
						Building software that works — and keeps working — takes more than code. It takes
						judgment earned over decades. Lucentary pairs you with senior experts, led by Chad
						Greer, so your project gets the expertise and excellence it deserves from the start.
					</p>
					<ul class="hero-points">
						<li class="hero-point">
							<span class="check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							Senior experts on your project — not junior guesswork
						</li>
						<li class="hero-point">
							<span class="check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							A clear plan on your first call — no obligation
						</li>
						<li class="hero-point">
							<span class="check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							We'll tell you the truth, even if it costs us the sale
						</li>
					</ul>
				</div>

				<div class="book-card">
					<transition name="swap" mode="out-in">
						<div v-if="!data.requestMoreInfo.isSuccess" key="form" class="book-form">
							<div class="book-head">
								<span class="book-badge">Free · 30 minutes · No obligation</span>
								<h2 class="book-title">Book your free consultation</h2>
								<p class="book-sub">Tell Chad a bit about your project and he'll reach out to schedule your call.</p>
							</div>
							<div class="form-field">
								<label for="bookName">Name</label>
								<input id="bookName" v-model="data.requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="form-field">
								<label for="bookEmail">Email or phone number</label>
								<input id="bookEmail" v-model="data.requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="form-field">
								<label for="bookText">What do you need help with?</label>
								<textarea id="bookText" v-model="data.requestMoreInfo.contactInfo.text" rows="3" :disabled="data.requestMoreInfo.isProcessing"></textarea>
							</div>
							<div v-if="data.requestMoreInfo.validationErrors.length > 0" class="form-error" role="alert">
							<p class="form-error-title">Please fix the following:</p>
							<ul class="form-error-list">
								<li v-for="(validationError, validationErrorIndex) in data.requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
							</ul>
						</div>
						<p v-else-if="data.requestMoreInfo.isError" class="form-error" role="alert">Something went wrong sending your request. Please try again.</p>
							<button type="button" class="btn btn-primary btn-block" :disabled="data.requestMoreInfo.isProcessing" @click="submitConsultation"><span v-if="data.requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ data.requestMoreInfo.isProcessing ? "Sending…" : "Book my free consultation" }}</button>
							<p class="book-note">No spam, no pressure. Just a straight conversation.</p>
						</div>

						<div v-else key="success" class="book-success">
							<span class="success-icon" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							<h2 class="book-title">Request received</h2>
							<p class="book-sub">Thanks for reaching out — Chad Greer will get in touch with you shortly to schedule your free consultation.</p>
						</div>
					</transition>
				</div>
			</div>
		</section>

		<section class="strip">
			<div class="strip-inner">
				<span class="strip-stars" aria-hidden="true">★★★★★</span>
				<p class="strip-text">Trusted by founders, CTOs, and teams who needed software done right — backed by 20+ years of shipping real systems.</p>
			</div>
		</section>

		<section class="compare">
			<div class="section-inner">
				<header class="block-header">
					<span class="eyebrow">Why it matters</span>
					<h2 class="block-heading">There's the usual way to build software. Then there's ours.</h2>
				</header>
				<div class="compare-grid">
					<div class="compare-col compare-old">
						<h3 class="compare-title">The usual way</h3>
						<ul class="compare-list">
							<li v-for="item in oldWay" :key="item" class="compare-item">
								<span class="mark mark-cross" aria-hidden="true">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
								</span>
								{{ item }}
							</li>
						</ul>
					</div>
					<div class="compare-col compare-new">
						<h3 class="compare-title compare-title-new">The Lucentary way</h3>
						<ul class="compare-list">
							<li v-for="item in lucentaryWay" :key="item" class="compare-item compare-item-new">
								<span class="mark mark-check" aria-hidden="true">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
								</span>
								{{ item }}
							</li>
						</ul>
					</div>
				</div>
			</div>
		</section>

		<section class="value">
			<div class="section-inner">
				<div class="value-card">
					<div class="value-copy">
						<span class="eyebrow">The offer</span>
						<h2 class="value-heading">Everything you get when you work with Lucentary</h2>
						<p class="value-lead">
							We take a limited number of new clients each month so every project gets senior
							attention — and new clients get 50% off their first 6 weeks.
						</p>
						<button type="button" class="btn btn-primary btn-large" @click="openConsultation">Book your free consultation</button>
					</div>
					<ul class="value-list">
						<li v-for="valueItem in valueIncludes" :key="valueItem" class="value-item">
							<span class="check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							{{ valueItem }}
						</li>
					</ul>
				</div>
			</div>
		</section>

		<section class="proof">
			<div class="section-inner">
				<header class="block-header">
					<span class="eyebrow">In their own words</span>
					<h2 class="block-heading">The people who've worked with Chad</h2>
				</header>
				<div class="testimonials-carousel">
					<TestimonialCarousel :items="testimonials">
						<template #default="{ item }">
							<figure class="testimonial-card">
								<div class="testimonial-stars" aria-hidden="true">★★★★★</div>
								<blockquote class="testimonial-quote">{{ item.quote }}</blockquote>
								<figcaption class="testimonial-author">
									<span class="testimonial-avatar" aria-hidden="true">{{ item.author.charAt(0) }}</span>
									<span>
										<span class="testimonial-name">{{ item.author }}</span>
										<span class="testimonial-role">{{ item.role }}</span>
									</span>
								</figcaption>
							</figure>
						</template>
					</TestimonialCarousel>
				</div>
			</div>
		</section>

		<section class="faq">
			<div class="section-inner section-inner-narrow">
				<header class="block-header">
					<span class="eyebrow">Questions</span>
					<h2 class="block-heading">Everything you might be wondering</h2>
				</header>
				<div class="faq-list">
					<div v-for="(faq, faqIndex) in faqs" :key="faq.question" class="faq-item" :class="{ 'faq-item-open': data.openFaqIndex === faqIndex }">
						<button type="button" class="faq-question" :aria-expanded="data.openFaqIndex === faqIndex" @click="toggleFaq(faqIndex)">
							<span>{{ faq.question }}</span>
							<span class="faq-icon" aria-hidden="true"></span>
						</button>
						<div v-show="data.openFaqIndex === faqIndex" class="faq-answer">
							<p>{{ faq.answer }}</p>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section class="final-cta">
			<div class="section-inner">
				<div class="final-cta-card">
					<h2 class="final-cta-title">One call. A clear plan. No risk.</h2>
					<p class="final-cta-lead">
						Get expert eyes on your project and a clear path forward — free,
						and useful even if we never work together.
					</p>
					<button type="button" class="btn btn-light btn-large" @click="openConsultation">Book your free consultation</button>
					<p class="final-cta-note">Free · 30 minutes · No obligation</p>
				</div>
			</div>
		</section>

		<footer class="site-footer">
			<div class="site-footer-inner">
				<span class="brand">
					<img class="brand-logo" :src="largeLucentaryLogoUrl" alt="Lucentary" />
				</span>
				<p class="footer-copy">Professional software services. &copy; {{ new Date().getFullYear() }} Lucentary.</p>
			</div>
		</footer>

		<transition name="modal-fade">
			<div v-if="data.requestMoreInfo.isShowing" class="modal-overlay" @click.self="closeConsultation">
				<div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="consultationModalTitle" @click.stop>
					<button type="button" class="modal-close" aria-label="Close" @click="closeConsultation">&times;</button>
					<div class="modal-scroll">
					<template v-if="!data.requestMoreInfo.isSuccess">
					<span class="modal-badge">Free · 30 minutes · No obligation</span>
					<h2 id="consultationModalTitle" class="modal-title">Book your free consultation</h2>
					<p class="modal-description">Tell us a little about your situation and Chad will be in touch to schedule your call.</p>
					<div class="modal-field">
						<label for="modalName">Name</label>
						<input id="modalName" v-model="data.requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="data.requestMoreInfo.isProcessing" />
					</div>
					<div class="modal-field">
						<label for="modalContact">Email or phone number</label>
						<input id="modalContact" v-model="data.requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="data.requestMoreInfo.isProcessing" />
					</div>
					<div class="modal-field">
						<label for="modalText">What do you need help with?</label>
						<textarea id="modalText" v-model="data.requestMoreInfo.contactInfo.text" rows="4" :disabled="data.requestMoreInfo.isProcessing"></textarea>
					</div>
					<div v-if="data.requestMoreInfo.validationErrors.length > 0" class="modal-feedback modal-feedback-error" role="alert">
						<p class="modal-feedback-title">Please fix the following:</p>
						<ul class="modal-feedback-list">
							<li v-for="(validationError, validationErrorIndex) in data.requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
						</ul>
					</div>
					<p v-else-if="data.requestMoreInfo.isError" class="modal-feedback modal-feedback-error" role="alert">Something went wrong sending your request. Please try again.</p>
					<button type="button" class="btn btn-primary btn-block" :disabled="data.requestMoreInfo.isProcessing" @click="submitConsultation"><span v-if="data.requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ data.requestMoreInfo.isProcessing ? "Sending…" : "Request my free call" }}</button>
					</template>
					<div v-else class="modal-success">
						<span class="success-check" aria-hidden="true">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
						</span>
						<h2 class="modal-title">Request received</h2>
						<p class="modal-description">Thanks for reaching out — Chad Greer will get in touch with you shortly to schedule your free consultation.</p>
					</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
	.promo-c {
		--bg: #eef3fb;
		--surface: #ffffff;
		--surface-muted: #f3f7fd;
		--ink: #0f2142;
		--ink-soft: #45556f;
		--ink-muted: #6f7f99;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dde8fc;
		--sky: #cfe0fb;
		--border: #e1e9f6;
		--border-strong: #cad7ea;
		--navy: #0d1b34;
		--rose: #c0455a;

		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
		color: var(--ink);
		background: var(--bg);
		--carousel-accent: var(--blue);
		--carousel-dot: #c2d3ee;
		--carousel-arrow-bg: #ffffff;
		overflow-x: hidden;
	}

	.site-header {
		position: sticky;
		top: 0;
		z-index: 900;
		background: rgba(238, 243, 251, 0.85);
		backdrop-filter: blur(12px);
		-webkit-backdrop-filter: blur(12px);
		border-bottom: 1px solid var(--border);
	}

	.site-header-inner {
		max-width: 1180px;
		margin: 0 auto;
		padding: 0.85rem 1.5rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
	}

	.brand {
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;
		text-decoration: none;
		color: var(--navy);
	}

	.brand-logo {
		height: 96px;
		width: auto;
		display: block;
	}

	.cta-text-short {
		display: none;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.8rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		border: 2px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 30px -14px rgba(37, 99, 235, 0.75);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 38px -16px rgba(37, 99, 235, 0.8);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 3px;
	}

	.btn-light {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 16px 34px -16px rgba(0, 0, 0, 0.45);
	}

	.btn-light:hover {
		transform: translateY(-2px);
		background: #eef4ff;
	}

	.btn-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.7);
		outline-offset: 3px;
	}

	.btn-large {
		padding: 1.1rem 2.2rem;
		font-size: 1.15rem;
		border-radius: 14px;
	}

	.btn-compact {
		padding: 0.7rem 1.3rem;
		font-size: 0.98rem;
	}

	.btn-block {
		width: 100%;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		animation: btn-spin 0.7s linear infinite;
	}

	@keyframes btn-spin {
		to {
			transform: rotate(360deg);
		}
	}

	.modal-success {
		text-align: center;
		padding: 0.75rem 0.25rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.hero {
		position: relative;
		padding: clamp(2.5rem, 6vw, 5rem) 1.5rem clamp(3rem, 6vw, 5rem);
		overflow: hidden;
		isolation: isolate;
		scroll-margin-top: 80px;
	}

	.hero-decoration {
		position: absolute;
		inset: 0;
		z-index: -1;
		pointer-events: none;
	}

	.blob {
		position: absolute;
		border-radius: 50%;
		filter: blur(12px);
		opacity: 0.5;
	}

	.blob-one {
		top: -160px;
		right: -120px;
		width: 460px;
		height: 460px;
		background: radial-gradient(circle at 30% 30%, var(--sky), transparent 70%);
	}

	.blob-two {
		bottom: -180px;
		left: -140px;
		width: 400px;
		height: 400px;
		background: radial-gradient(circle at 60% 40%, var(--blue-soft), transparent 70%);
	}

	.hero-inner {
		max-width: 1180px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 1.05fr 0.95fr;
		gap: clamp(2rem, 4vw, 3.5rem);
		align-items: center;
	}

	.offer-pill {
		display: inline-block;
		padding: 0.5rem 1.15rem;
		border-radius: 999px;
		background: var(--blue);
		color: #ffffff;
		font-size: 0.95rem;
		font-weight: 700;
		margin-bottom: 1.5rem;
		box-shadow: 0 12px 26px -14px rgba(37, 99, 235, 0.7);
	}

	.hero-title {
		font-size: clamp(2.4rem, 4.6vw, 4rem);
		line-height: 1.04;
		font-weight: 800;
		letter-spacing: -0.025em;
		color: var(--navy);
		margin: 0 0 1.4rem;
	}

	.hero-lead {
		font-size: clamp(1.1rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: var(--ink-soft);
		max-width: 54ch;
		margin: 0 0 1.75rem;
	}

	.hero-points {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 0.8rem;
	}

	.hero-point {
		display: flex;
		align-items: flex-start;
		gap: 0.7rem;
		font-size: 1.05rem;
		font-weight: 500;
		line-height: 1.4;
		color: var(--ink);
	}

	.check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		flex-shrink: 0;
		margin-top: 0.1rem;
	}

	.check svg {
		width: 14px;
		height: 14px;
	}

	.book-card {
		background: var(--surface);
		border: 1px solid var(--border);
		border-radius: 24px;
		padding: clamp(1.75rem, 3vw, 2.4rem);
		box-shadow: 0 36px 72px -38px rgba(13, 27, 52, 0.55);
	}

	.book-head {
		margin-bottom: 1.5rem;
	}

	.book-badge {
		display: inline-block;
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.85rem;
		font-weight: 700;
		margin-bottom: 1rem;
	}

	.book-title {
		font-size: clamp(1.5rem, 2.2vw, 1.95rem);
		font-weight: 800;
		letter-spacing: -0.01em;
		color: var(--navy);
		margin: 0 0 0.5rem;
	}

	.book-sub {
		font-size: 1rem;
		line-height: 1.5;
		color: var(--ink-soft);
		margin: 0;
	}

	.form-field {
		margin-bottom: 1rem;
		text-align: left;
	}

	.form-field label {
		display: block;
		font-size: 0.95rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.45rem;
	}

	.form-field input,
	.form-field textarea {
		width: 100%;
		padding: 0.8rem 1rem;
		border-radius: 12px;
		border: 2px solid var(--border-strong);
		background: var(--surface-muted);
		font-family: inherit;
		font-size: 1.05rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.form-field textarea {
		resize: vertical;
	}

	.form-field input:focus,
	.form-field textarea:focus {
		outline: none;
		background: var(--surface);
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
	}

	.form-error {
		margin: 0 0 1rem;
		padding: 0.7rem 0.9rem;
		border-radius: 10px;
		background: #fbe9ec;
		color: #9c1f2e;
		font-size: 0.95rem;
		font-weight: 600;
	}

	.form-error-title {
		margin: 0 0 0.35rem;
		font-weight: 700;
	}

	.form-error-list {
		margin: 0;
		padding-left: 1.2rem;
		font-weight: 500;
	}

	.form-error-list li {
		margin-bottom: 0.2rem;
	}

	.book-note {
		margin: 1rem 0 0;
		font-size: 0.9rem;
		color: var(--ink-muted);
		text-align: center;
	}

	.book-success {
		text-align: center;
		padding: 1.5rem 0.5rem;
	}

	.success-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 64px;
		height: 64px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.25rem;
	}

	.success-icon svg {
		width: 32px;
		height: 32px;
	}

	.strip {
		background: var(--navy);
		padding: 1.5rem;
	}

	.strip-inner {
		max-width: 1000px;
		margin: 0 auto;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.9rem;
		flex-wrap: wrap;
		text-align: center;
	}

	.strip-stars {
		color: #f5a623;
		font-size: 1.1rem;
		letter-spacing: 0.08em;
	}

	.strip-text {
		font-size: clamp(1rem, 1.3vw, 1.15rem);
		font-weight: 500;
		line-height: 1.5;
		color: rgba(255, 255, 255, 0.92);
		margin: 0;
		max-width: 70ch;
	}

	.section-inner {
		max-width: 1180px;
		margin: 0 auto;
	}

	.section-inner-narrow {
		max-width: 820px;
	}

	.block-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.eyebrow {
		display: inline-block;
		font-size: 0.9rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 1rem;
	}

	.block-heading {
		font-size: clamp(1.9rem, 3.2vw, 3rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		line-height: 1.1;
		color: var(--navy);
		margin: 0 auto;
		max-width: 24ch;
	}

	.compare {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.compare-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1.5rem;
		align-items: start;
	}

	.compare-col {
		padding: clamp(1.75rem, 3vw, 2.5rem);
		border-radius: 22px;
		border: 1px solid var(--border);
	}

	.compare-old {
		background: var(--bg);
	}

	.compare-new {
		background: linear-gradient(160deg, var(--blue-soft) 0%, #eef4ff 100%);
		border-color: var(--blue);
		box-shadow: 0 24px 50px -34px rgba(37, 99, 235, 0.5);
	}

	.compare-title {
		font-size: 1.3rem;
		font-weight: 700;
		color: var(--ink-muted);
		margin: 0 0 1.5rem;
	}

	.compare-title-new {
		color: var(--blue-deep);
	}

	.compare-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 1rem;
	}

	.compare-item {
		display: flex;
		align-items: flex-start;
		gap: 0.8rem;
		font-size: 1.08rem;
		line-height: 1.45;
		color: var(--ink-soft);
	}

	.compare-item-new {
		color: var(--ink);
		font-weight: 500;
	}

	.mark {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;
		border-radius: 50%;
		flex-shrink: 0;
		margin-top: 0.05rem;
	}

	.mark svg {
		width: 15px;
		height: 15px;
	}

	.mark-cross {
		background: rgba(192, 69, 90, 0.12);
		color: var(--rose);
	}

	.mark-check {
		background: var(--blue);
		color: #ffffff;
	}

	.value {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--bg);
	}

	.value-card {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: clamp(2rem, 5vw, 4rem);
		align-items: center;
		padding: clamp(2rem, 4vw, 3.5rem);
		border-radius: 28px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 30px 64px -40px rgba(13, 27, 52, 0.45);
	}

	.value-heading {
		font-size: clamp(1.8rem, 3vw, 2.6rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		line-height: 1.12;
		color: var(--navy);
		margin: 0 0 1.1rem;
	}

	.value-lead {
		font-size: clamp(1.05rem, 1.4vw, 1.2rem);
		line-height: 1.6;
		color: var(--ink-soft);
		margin: 0 0 1.85rem;
	}

	.value-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 1.1rem;
	}

	.value-item {
		display: flex;
		align-items: flex-start;
		gap: 0.85rem;
		font-size: 1.1rem;
		font-weight: 500;
		line-height: 1.45;
		color: var(--ink);
	}

	.proof {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
	}

	.testimonials-carousel {
		margin-top: 1rem;
	}

	.testimonial-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 2rem 1.85rem;
		border-radius: 18px;
		background: var(--bg);
		border: 1px solid var(--border);
		margin: 0;
		box-sizing: border-box;
	}

	.testimonial-stars {
		color: #f5a623;
		font-size: 1.05rem;
		letter-spacing: 0.12em;
		margin-bottom: 1rem;
	}

	.testimonial-quote {
		flex: 1;
		font-size: 1.02rem;
		line-height: 1.7;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.testimonial-author {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.testimonial-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 44px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 1.05rem;
		font-weight: 800;
		flex-shrink: 0;
	}

	.testimonial-name {
		display: block;
		font-size: 1rem;
		font-weight: 700;
		color: var(--navy);
	}

	.testimonial-role {
		display: block;
		font-size: 0.9rem;
		color: var(--ink-muted);
	}

	.faq {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--bg);
	}

	.faq-list {
		display: grid;
		gap: 1rem;
	}

	.faq-item {
		background: var(--surface);
		border: 1px solid var(--border);
		border-radius: 16px;
		overflow: hidden;
		transition: border-color 0.2s ease, box-shadow 0.2s ease;
	}

	.faq-item-open {
		border-color: var(--blue);
		box-shadow: 0 18px 40px -28px rgba(37, 99, 235, 0.5);
	}

	.faq-question {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 1.4rem 1.6rem;
		background: transparent;
		border: none;
		cursor: pointer;
		font-family: inherit;
		font-size: 1.12rem;
		font-weight: 600;
		text-align: left;
		color: var(--navy);
	}

	.faq-question:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: -3px;
	}

	.faq-icon {
		position: relative;
		width: 18px;
		height: 18px;
		flex-shrink: 0;
	}

	.faq-icon::before,
	.faq-icon::after {
		content: "";
		position: absolute;
		background: var(--blue);
		border-radius: 2px;
		transition: transform 0.25s ease, opacity 0.25s ease;
	}

	.faq-icon::before {
		top: 8px;
		left: 0;
		width: 18px;
		height: 2px;
	}

	.faq-icon::after {
		top: 0;
		left: 8px;
		width: 2px;
		height: 18px;
	}

	.faq-item-open .faq-icon::after {
		transform: scaleY(0);
		opacity: 0;
	}

	.faq-answer {
		padding: 0 1.6rem 1.5rem;
	}

	.faq-answer p {
		margin: 0;
		font-size: 1.05rem;
		line-height: 1.65;
		color: var(--ink-soft);
	}

	.final-cta {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
	}

	.final-cta-card {
		max-width: 900px;
		margin: 0 auto;
		text-align: center;
		padding: clamp(2.75rem, 5vw, 4.5rem);
		border-radius: 28px;
		background: linear-gradient(135deg, var(--blue) 0%, #1e40af 100%);
		color: #ffffff;
		box-shadow: 0 34px 70px -38px rgba(37, 99, 235, 0.7);
	}

	.final-cta-title {
		font-size: clamp(2rem, 3.6vw, 3.2rem);
		font-weight: 800;
		letter-spacing: -0.025em;
		line-height: 1.08;
		margin: 0 auto 1.25rem;
		max-width: 18ch;
		color: #ffffff;
	}

	.final-cta-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.25rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 52ch;
		margin: 0 auto 2rem;
	}

	.final-cta-note {
		margin: 1.25rem 0 0;
		font-size: 0.95rem;
		color: rgba(255, 255, 255, 0.78);
		letter-spacing: 0.02em;
	}

	.site-footer {
		padding: 2.5rem 1.5rem;
		background: var(--bg);
		border-top: 1px solid var(--border);
	}

	.site-footer-inner {
		max-width: 1180px;
		margin: 0 auto;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
	}

	.footer-copy {
		font-size: 0.92rem;
		color: var(--ink-muted);
		margin: 0;
	}

	.modal-overlay {
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(13, 27, 52, 0.55);
		backdrop-filter: blur(5px);
		-webkit-backdrop-filter: blur(5px);
		cursor: pointer;
		z-index: 100000;
	}

	.modal-dialog {
		position: relative;
		width: 100%;
		max-width: 520px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow: hidden;
		border-radius: 22px;
		background: var(--surface);
		box-shadow: 0 40px 90px -30px rgba(13, 27, 52, 0.6);
		cursor: default;
		pointer-events: auto;
	}

	.modal-scroll {
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(2.25rem, 4vw, 2.85rem) clamp(1.75rem, 3vw, 2.4rem);
	}

	.modal-close {
		position: absolute;
		top: 1.1rem;
		right: 1.25rem;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.9rem;
		line-height: 1;
		border-radius: 12px;
		border: none;
		background: var(--surface-muted);
		color: var(--ink-muted);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;
	}

	.modal-close:hover {
		background: var(--blue-soft);
		color: var(--blue-deep);
	}

	.modal-badge {
		display: inline-block;
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.85rem;
		font-weight: 700;
	}

	.modal-title {
		font-size: clamp(1.6rem, 2.4vw, 2rem);
		font-weight: 800;
		letter-spacing: -0.01em;
		color: var(--navy);
		margin: 0.75rem 0 0.6rem;
	}

	.modal-description {
		font-size: 1.02rem;
		line-height: 1.55;
		color: var(--ink-soft);
		margin: 0 0 1.75rem;
	}

	.modal-field {
		margin-bottom: 1.2rem;
		text-align: left;
	}

	.modal-field label {
		display: block;
		font-size: 1rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.5rem;
	}

	.modal-field input,
	.modal-field textarea {
		width: 100%;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		border: 2px solid var(--border-strong);
		background: var(--bg);
		font-family: inherit;
		font-size: 1.05rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.modal-field textarea {
		resize: vertical;
	}

	.modal-field input:focus,
	.modal-field textarea:focus {
		outline: none;
		background: var(--surface);
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
	}

	.modal-feedback {
		margin: 0 0 1.2rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.98rem;
		font-weight: 500;
		text-align: left;
	}

	.modal-feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.modal-feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.modal-feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.modal-feedback-list li {
		margin-bottom: 0.2rem;
	}

	.toast {
		position: fixed;
		left: 50%;
		bottom: 2rem;
		transform: translateX(-50%);
		max-width: 92vw;
		padding: 1.05rem 1.6rem;
		border-radius: 14px;
		font-size: 1.02rem;
		font-weight: 600;
		box-shadow: 0 18px 40px -20px rgba(13, 27, 52, 0.5);
		z-index: 1100;
	}

	.toast-success {
		background: #0e7490;
		color: #ffffff;
	}

	.modal-fade-enter-active,
	.modal-fade-leave-active {
		transition: opacity 0.25s ease;
	}

	.modal-fade-enter-active .modal-dialog,
	.modal-fade-leave-active .modal-dialog {
		transition: transform 0.25s ease, opacity 0.25s ease;
	}

	.modal-fade-enter-from,
	.modal-fade-leave-to {
		opacity: 0;
	}

	.modal-fade-enter-from .modal-dialog,
	.modal-fade-leave-to .modal-dialog {
		transform: translateY(14px) scale(0.98);
		opacity: 0;
	}

	.toast-fade-enter-active,
	.toast-fade-leave-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}

	.toast-fade-enter-from,
	.toast-fade-leave-to {
		opacity: 0;
		transform: translateX(-50%) translateY(12px);
	}

	.swap-enter-active,
	.swap-leave-active {
		transition: opacity 0.25s ease, transform 0.25s ease;
	}

	.swap-enter-from {
		opacity: 0;
		transform: translateY(8px);
	}

	.swap-leave-to {
		opacity: 0;
		transform: translateY(-8px);
	}

	@media (max-width: 900px) {
		.hero-inner,
		.compare-grid,
		.value-card {
			grid-template-columns: 1fr;
		}

		.book-card {
			order: -1;
		}
	}

	@media (max-width: 640px) {
		.brand-logo {
			height: 40px;
		}

		.modal-close {
			top: 0.9rem;
			right: 0.9rem;
			width: 36px;
			height: 36px;
			font-size: 1.7rem;
		}

		.modal-scroll {
			padding-top: 3.4rem;
		}

		.cta-text-full {
			display: none;
		}

		.cta-text-short {
			display: inline;
		}

		.offer-pill {
			display: block;
			text-align: center;
			border-radius: 16px;
			padding: 0.6rem 1rem;
			font-size: 0.9rem;
			line-height: 1.4;
		}

		.btn-large {
			width: 100%;
		}

		.strip-inner {
			flex-direction: column;
			gap: 0.5rem;
		}

		.site-header .btn-compact {
			padding: 0.6rem 1rem;
			font-size: 0.9rem;
		}
	}
</style>